import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, setDoc, query, orderBy, writeBatch } from 'firebase/firestore';
import { db } from '../../../firebase';
import { createApiCall } from '../../../services/apiClient';
import type {
  LearningSection,
  LearningLesson,
  LearningLessonPage,
  LearningLessonQuestionPage,
  LessonPage,
  CreateSectionData,
  CreateLessonData,
  CreateAnyPageData,
  UpdateSectionData,
  UpdateLessonData,
  UpdateAnyPageData,
  AdminGetLessonRequest,
  LessonPreviewResponse,
} from './learningTypes';

export type CollectionName = 'learning-sections' | 'learning-sections-v2';

export class AdminLearningService {
  private readonly collection: CollectionName;

  constructor(collection: CollectionName = 'learning-sections') {
    this.collection = collection;
  }

  // ==================== SECTIONS ====================

  async getAllSections(): Promise<LearningSection[]> {
    try {
      const sectionsRef = collection(db, this.collection);
      const sectionsQuery = query(sectionsRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(sectionsQuery);

      const sections: LearningSection[] = [];
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        sections.push({
          uid: docSnap.id,
          title: data.title,
          subtitle: data.subtitle,
          imageSrc: data.imageSrc,
          order: data.order || 0,
          status: data.status,
          isSupportOnly: data.isSupportOnly || false,
          lessons: data.lessons || [],
        });
      }

      return sections;
    } catch (error) {
      console.error('Error fetching sections:', error);
      throw error;
    }
  }

  async getSection(sectionId: string): Promise<LearningSection | null> {
    try {
      const sectionRef = doc(db, this.collection, sectionId);
      const sectionSnap = await getDoc(sectionRef);

      if (!sectionSnap.exists()) {
        return null;
      }

      const data = sectionSnap.data();
      return {
        uid: sectionSnap.id,
        title: data.title,
        subtitle: data.subtitle,
        imageSrc: data.imageSrc,
        order: data.order || 0,
        status: data.status,
        isSupportOnly: data.isSupportOnly || false,
        lessons: data.lessons || [],
      };
    } catch (error) {
      console.error('Error fetching section:', error);
      throw error;
    }
  }

  async createSection(sectionData: CreateSectionData): Promise<string> {
    try {
      const sectionsRef = collection(db, this.collection);

      if (sectionData.id) {
        const sectionRef = doc(sectionsRef, sectionData.id);
        const dataToSave = {
          title: sectionData.title,
          subtitle: sectionData.subtitle,
          imageSrc: sectionData.imageSrc,
          status: sectionData.status,
          isSupportOnly: sectionData.isSupportOnly,
          order: sectionData.order,
          lessons: [],
        };
        await setDoc(sectionRef, dataToSave);
        return sectionData.id;
      } else {
        const docRef = await addDoc(sectionsRef, {
          ...sectionData,
          lessons: [],
        });
        return docRef.id;
      }
    } catch (error) {
      console.error('Error creating section:', error);
      throw error;
    }
  }

  async updateSection(sectionId: string, sectionData: UpdateSectionData): Promise<void> {
    try {
      const sectionRef = doc(db, this.collection, sectionId);
      await updateDoc(sectionRef, sectionData);
    } catch (error) {
      console.error('Error updating section:', error);
      throw error;
    }
  }

  async deleteSection(sectionId: string): Promise<void> {
    try {
      const batch = writeBatch(db);

      const lessonsRef = collection(db, this.collection, sectionId, 'lessons');
      const lessonsSnapshot = await getDocs(lessonsRef);

      for (const lessonDoc of lessonsSnapshot.docs) {
        const pagesRef = collection(db, this.collection, sectionId, 'lessons', lessonDoc.id, 'pages');
        const pagesSnapshot = await getDocs(pagesRef);

        pagesSnapshot.docs.forEach((pageDoc) => {
          batch.delete(pageDoc.ref);
        });

        batch.delete(lessonDoc.ref);
      }

      const sectionRef = doc(db, this.collection, sectionId);
      batch.delete(sectionRef);

      await batch.commit();
    } catch (error) {
      console.error('Error deleting section:', error);
      throw error;
    }
  }

  // ==================== LESSONS ====================

  async getLessonsInSection(sectionId: string): Promise<LearningLesson[]> {
    try {
      const lessonsRef = collection(db, this.collection, sectionId, 'lessons');
      const lessonsQuery = query(lessonsRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(lessonsQuery);

      const lessons: LearningLesson[] = [];
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        lessons.push({
          uid: docSnap.id,
          title: data.title,
          imageSrc: data.imageSrc,
          description: data.description,
          isFree: data.isFree === true,
          order: data.order || 0,
          status: data.status,
          timeMinutes: data.timeMinutes,
          type: data.type,
          color: data.color,
        });
      }

      return lessons;
    } catch (error) {
      console.error('Error fetching lessons:', error);
      throw error;
    }
  }

  async getLesson(sectionId: string, lessonId: string): Promise<LearningLesson | null> {
    try {
      const lessonRef = doc(db, this.collection, sectionId, 'lessons', lessonId);
      const lessonSnap = await getDoc(lessonRef);

      if (!lessonSnap.exists()) {
        return null;
      }

      const data = lessonSnap.data();
      return {
        uid: lessonSnap.id,
        title: data.title,
        imageSrc: data.imageSrc,
        description: data.description,
        isFree: data.isFree === true,
        order: data.order || 0,
        status: data.status,
        timeMinutes: data.timeMinutes,
        type: data.type,
        color: data.color,
      };
    } catch (error) {
      console.error('Error fetching lesson:', error);
      throw error;
    }
  }

  async createLesson(sectionId: string, lessonData: CreateLessonData): Promise<string> {
    try {
      const lessonsRef = collection(db, this.collection, sectionId, 'lessons');
      let lessonId: string;

      if (lessonData.id) {
        const lessonRef = doc(lessonsRef, lessonData.id);
        const dataToSave = {
          title: lessonData.title,
          imageSrc: lessonData.imageSrc,
          description: lessonData.description,
          isFree: lessonData.isFree,
          timeMinutes: lessonData.timeMinutes,
          type: lessonData.type,
          status: lessonData.status,
          order: lessonData.order,
          color: lessonData.color,
        };
        await setDoc(lessonRef, dataToSave);
        lessonId = lessonData.id;
      } else {
        const docRef = await addDoc(lessonsRef, lessonData);
        lessonId = docRef.id;
      }

      await this.updateSectionLessonsArray(sectionId);

      return lessonId;
    } catch (error) {
      console.error('Error creating lesson:', error);
      throw error;
    }
  }

  async updateLesson(
    sectionId: string,
    lessonId: string,
    lessonData: UpdateLessonData
  ): Promise<void> {
    try {
      const lessonRef = doc(db, this.collection, sectionId, 'lessons', lessonId);
      await updateDoc(lessonRef, lessonData);

      await this.updateSectionLessonsArray(sectionId);
    } catch (error) {
      console.error('Error updating lesson:', error);
      throw error;
    }
  }

  async deleteLesson(sectionId: string, lessonId: string): Promise<void> {
    try {
      const batch = writeBatch(db);

      const pagesRef = collection(db, this.collection, sectionId, 'lessons', lessonId, 'pages');
      const pagesSnapshot = await getDocs(pagesRef);

      pagesSnapshot.docs.forEach((pageDoc) => {
        batch.delete(pageDoc.ref);
      });

      const lessonRef = doc(db, this.collection, sectionId, 'lessons', lessonId);
      batch.delete(lessonRef);

      await batch.commit();

      await this.updateSectionLessonsArray(sectionId);
    } catch (error) {
      console.error('Error deleting lesson:', error);
      throw error;
    }
  }

  async duplicateLesson(sectionId: string, lessonId: string): Promise<string> {
    try {
      const originalLesson = await this.getLesson(sectionId, lessonId);
      if (!originalLesson) {
        throw new Error('Original lesson not found');
      }

      const originalPages = await this.getPagesInLesson(sectionId, lessonId);

      const duplicateLessonData: CreateLessonData = {
        title: `${originalLesson.title} (Copy)`,
        imageSrc: originalLesson.imageSrc,
        description: originalLesson.description,
        isFree: originalLesson.isFree,
        timeMinutes: originalLesson.timeMinutes,
        type: originalLesson.type,
        status: 'draft',
        order: originalLesson.order ? originalLesson.order + 1 : 1,
        color: originalLesson.color,
      };

      const newLessonId = await this.createLesson(sectionId, duplicateLessonData);

      for (const originalPage of originalPages) {
        let duplicatePageData: CreateAnyPageData;

        if (originalPage.type === 'question') {
          duplicatePageData = {
            content: originalPage.content,
            buttonText: originalPage.buttonText,
            order: originalPage.order,
            type: 'question',
            aiFriendlyQuestion: originalPage.aiFriendlyQuestion || '',
            options: originalPage.options.map((option) => ({
              uid: crypto.randomUUID(),
              text: option.text,
              aiFriendlyText: option.aiFriendlyText || '',
            })),
            correctOptionIds: [],
            correctAnswerReaction: originalPage.correctAnswerReaction,
            incorrectAnswerReaction: originalPage.incorrectAnswerReaction,
            shouldSaveAnswer: originalPage.shouldSaveAnswer,
          };
        } else {
          duplicatePageData = {
            content: originalPage.content,
            buttonText: originalPage.buttonText,
            order: originalPage.order,
            type: 'page',
          };
        }

        if (originalPage.type === 'question' && duplicatePageData.type === 'question') {
          const originalOptions = originalPage.options;
          const newOptions = duplicatePageData.options;

          const optionIdMap = new Map<string, string>();
          originalOptions.forEach((originalOption, index) => {
            if (newOptions[index]) {
              optionIdMap.set(originalOption.uid, newOptions[index].uid);
            }
          });

          duplicatePageData.correctOptionIds = originalPage.correctOptionIds
            .map((oldId) => optionIdMap.get(oldId))
            .filter((newId): newId is string => newId !== undefined);
        }

        await this.createPage(sectionId, newLessonId, duplicatePageData);
      }

      return newLessonId;
    } catch (error) {
      console.error('Error duplicating lesson:', error);
      throw error;
    }
  }

  // ==================== PAGES ====================

  async getPagesInLesson(sectionId: string, lessonId: string): Promise<LessonPage[]> {
    try {
      const pagesRef = collection(db, this.collection, sectionId, 'lessons', lessonId, 'pages');
      const pagesQuery = query(pagesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(pagesQuery);

      const pages: LessonPage[] = [];
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const pageType = data.type || 'page';

        if (pageType === 'question') {
          pages.push({
            uid: docSnap.id,
            originalUid: docSnap.id,
            content: data.content,
            buttonText: data.buttonText || '',
            order: data.order || 0,
            type: 'question',
            shouldSaveAnswer: data.shouldSaveAnswer || false,
            aiFriendlyQuestion: data.aiFriendlyQuestion || '',
            options: data.options || [],
            correctOptionIds: data.correctOptionIds || [],
            correctAnswerReaction: data.correctAnswerReaction,
            incorrectAnswerReaction: data.incorrectAnswerReaction,
          } as LearningLessonQuestionPage);
        } else {
          pages.push({
            uid: docSnap.id,
            originalUid: docSnap.id,
            content: data.content,
            buttonText: data.buttonText || '',
            order: data.order || 0,
            type: 'page',
          } as LearningLessonPage);
        }
      }

      return pages;
    } catch (error) {
      console.error('Error fetching pages:', error);
      throw error;
    }
  }

  async createPage(
    sectionId: string,
    lessonId: string,
    pageData: CreateAnyPageData
  ): Promise<string> {
    try {
      const pagesRef = collection(db, this.collection, sectionId, 'lessons', lessonId, 'pages');

      if (pageData.uid) {
        const pageRef = doc(pagesRef, pageData.uid);
        const dataToSave = {
          content: pageData.content,
          buttonText: pageData.buttonText,
          order: pageData.order,
          type: pageData.type,
          ...(pageData.type === 'question' && {
            shouldSaveAnswer: pageData.shouldSaveAnswer,
            aiFriendlyQuestion: pageData.aiFriendlyQuestion,
            options: pageData.options,
            correctOptionIds: pageData.correctOptionIds,
            correctAnswerReaction: pageData.correctAnswerReaction,
            incorrectAnswerReaction: pageData.incorrectAnswerReaction,
          }),
        };
        await setDoc(pageRef, dataToSave);
        return pageData.uid;
      } else {
        const docRef = await addDoc(pagesRef, pageData);
        return docRef.id;
      }
    } catch (error) {
      console.error('Error creating page:', error);
      throw error;
    }
  }

  async updatePage(
    sectionId: string,
    lessonId: string,
    pageId: string,
    pageData: UpdateAnyPageData
  ): Promise<void> {
    try {
      const pageRef = doc(db, this.collection, sectionId, 'lessons', lessonId, 'pages', pageId);
      await updateDoc(pageRef, pageData);
    } catch (error) {
      console.error('Error updating page:', error);
      throw error;
    }
  }

  async deletePage(sectionId: string, lessonId: string, pageId: string): Promise<void> {
    try {
      const pageRef = doc(db, this.collection, sectionId, 'lessons', lessonId, 'pages', pageId);
      await deleteDoc(pageRef);
    } catch (error) {
      console.error('Error deleting page:', error);
      throw error;
    }
  }

  // ==================== HELPER METHODS ====================

  private async updateSectionLessonsArray(sectionId: string): Promise<void> {
    try {
      const lessons = await this.getLessonsInSection(sectionId);
      const sectionRef = doc(db, this.collection, sectionId);
      await updateDoc(sectionRef, { lessons });
    } catch (error) {
      console.error('Error updating section lessons array:', error);
      throw error;
    }
  }

  async getNextOrder(collectionPath: CollectionName): Promise<number> {
    try {
      const ref = collection(db, collectionPath);
      const snapshot = await getDocs(ref);

      let maxOrder = 0;
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.order && data.order > maxOrder) {
          maxOrder = data.order;
        }
      });

      return maxOrder + 1;
    } catch (error) {
      console.error('Error getting next order:', error);
      return 1;
    }
  }
}

const serviceCache = new Map<CollectionName, AdminLearningService>();

/**
 * Above the API's AI ladder (45s per attempt, one retry), so a preview whose
 * personalization needs a second attempt is not cut off client-side. Same deadline the
 * callable ran with.
 */
const LESSON_PREVIEW_TIMEOUT_MS = 120_000;

const adminGetLessonCall = createApiCall<AdminGetLessonRequest, LessonPreviewResponse>(
  'adminGetLesson',
  { timeoutMs: LESSON_PREVIEW_TIMEOUT_MS }
);

export const adminGetLessonPreview = async (
  sectionId: string,
  lessonId: string,
  userId: string
): Promise<LessonPreviewResponse> => adminGetLessonCall({ sectionId, lessonId, userId });

export const getAdminLearningService = (collectionName: CollectionName): AdminLearningService => {
  if (!serviceCache.has(collectionName)) {
    serviceCache.set(collectionName, new AdminLearningService(collectionName));
  }
  return serviceCache.get(collectionName)!;
};

export const adminLearningService = getAdminLearningService('learning-sections');
